import { useRef } from 'react';
import type { NewsAnalysis, NewsArticle } from './types';
import { ChevronLeft, ChevronRightIcon } from '../../../icons';
import NewsCard from './NewsCard';

interface NewsCoverageSectionProps {
  articles: NewsArticle[];
  total: number;
  analysis: NewsAnalysis | null;
  /** Section title — "News Articles" (YT, analysis is a separate card) or
   *  "News Coverage" (IG, this is the only news card). */
  title?: string;
}

const SCROLL_STEP = 280;

function bannerText(total: number, analysis: NewsAnalysis | null): string {
  if (!analysis) {
    return `${total} article${total === 1 ? '' : 's'} found`;
  }
  const adverseCount = analysis.article_sentiments.filter(
    (s) => s.sentiment === 'Negative',
  ).length;
  if (adverseCount === 0) return 'No adverse articles found';
  return `${adverseCount} adverse article${adverseCount === 1 ? '' : 's'} found`;
}

/**
 * "News Articles" section (Figma 2165-108189): title + subtitle + scroll
 * arrows, an adverse-articles banner, and the horizontal article slider.
 * The analysis lives in the separate "News coverage" card above this one.
 */
export default function NewsCoverageSection({
  articles,
  total,
  analysis,
  title = 'News Articles',
}: NewsCoverageSectionProps) {
  const scrollRef = useRef<HTMLDivElement>(null);

  const scrollBy = (delta: number) => {
    scrollRef.current?.scrollBy({ left: delta, behavior: 'smooth' });
  };

  return (
    <div className="mb-4 flex flex-col gap-5 rounded-2xl border border-border bg-card p-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-heading-h6 text-foreground">{title}</div>
          <div className="mt-0.5 text-body-sm-regular text-muted-foreground">
            Audience, Reach &amp; Reputation Overview
          </div>
        </div>

        {articles.length > 0 && (
          <div className="flex flex-none items-center gap-2">
            <ScrollArrow ariaLabel="Scroll left" onClick={() => scrollBy(-SCROLL_STEP)}>
              <ChevronLeft size={16} className="text-muted-foreground" />
            </ScrollArrow>
            <ScrollArrow ariaLabel="Scroll right" onClick={() => scrollBy(SCROLL_STEP)}>
              <ChevronRightIcon size={16} className="text-muted-foreground" />
            </ScrollArrow>
          </div>
        )}
      </div>

      <div className="w-full rounded-[10px] bg-muted p-3 text-body-sm-regular text-muted-foreground">
        {bannerText(total, analysis)}
      </div>

      {/* Articles — horizontal slider (native scroll) */}
      {articles.length > 0 && (
        <div ref={scrollRef} className="flex flex-row gap-4 overflow-x-auto pb-1">
          {articles.map((a, idx) => (
            <NewsCard key={`${a.link || a.title}-${idx}`} article={a} />
          ))}
        </div>
      )}
    </div>
  );
}

interface ScrollArrowProps {
  ariaLabel: string;
  onClick: () => void;
  children: React.ReactNode;
}

function ScrollArrow({ ariaLabel, onClick, children }: ScrollArrowProps) {
  return (
    <button
      type="button"
      aria-label={ariaLabel}
      onClick={onClick}
      className="flex size-10 items-center justify-center rounded-full border border-border bg-card hover:bg-accent"
    >
      {children}
    </button>
  );
}
